import { Box, Typography, Chip, Stack } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { locationsTags, rolesTags } from 'src/shared/helpers/filteredTags';
import { BusinessAreaResponse } from 'src/shared/services/api/response/businessAreasResponse';
import { TagsResponse } from 'src/shared/services/api/response/tagsResponse';
import { useMemo, useState } from 'react';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';

interface PageTagsProps {
  show?: boolean;
  tags?: TagsResponse[];
  businessAreaTags?: BusinessAreaResponse[];
}
export const MainPageTags = (props: PageTagsProps) => {
  const { show = true, tags = undefined, businessAreaTags = undefined } = props;
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);

  const combinedTags = useMemo(() => {
    const businessTags = (businessAreaTags ?? [])
      .map((tag) => ({ id: `business-${tag.id}`, name: tag.name }))
      .sort((a, b) => a.name.localeCompare(b.name, 'sv'));
    const locationTags = locationsTags(tags ?? [])
      .map((tag) => ({ id: `location-${tag.id}`, name: tag.name }))
      .sort((a, b) => a.name.localeCompare(b.name, 'sv'));
    const roleTags = rolesTags(tags ?? [])
      .map((tag) => ({ id: `role-${tag.id}`, name: tag.name }))
      .sort((a, b) => a.name.localeCompare(b.name, 'sv'));

    return [...businessTags, ...locationTags, ...roleTags];
  }, [businessAreaTags, tags]);

  const MAX_VISIBLE = 5;
  const visibleTags = expanded
    ? combinedTags
    : combinedTags.slice(0, MAX_VISIBLE);
  const hiddenCount = Math.max(combinedTags.length - MAX_VISIBLE, 0);
  const hasTags = combinedTags.length > 0;
  // Allow a bit more room on very small screens to avoid awkward clipping.
  const collapsedMaxHeight = { xs: 108, s: 68, m: 32 };

  return (
    <>
      {show && (
        <Box sx={{ textAlign: 'left' }}>
          {!hasTags && (
            <Stack direction='row' gap={0.5}>
              <Chip
                label={`${t('VisibleFor')} ${t('all').toLocaleLowerCase()}`}
                size='small'
                variant='outlined'
                sx={(theme) => ({
                  fontSize: 11,
                  borderColor: alpha(theme.palette.warning.main, 0.5),
                  color: alpha(theme.palette.warning.main, 0.9),
                  bgcolor: alpha(theme.palette.warning.main, 0.06),
                })}
              />
            </Stack>
          )}
          {hasTags && (
            <>
              <Typography sx={{ color: 'warning.main', fontSize: 11, mb: 0.5 }}>
                {t('VisibleFor')}
              </Typography>
              <Stack
                direction='row'
                flexWrap='wrap'
                gap={0.5}
                sx={
                  expanded
                    ? undefined
                    : { maxHeight: collapsedMaxHeight, overflow: 'hidden' }
                }
              >
                {visibleTags.map((tag) => {
                  return (
                    <Chip
                      key={tag.id}
                      label={tag.name}
                      size='small'
                      color='warning'
                      variant='outlined'
                      sx={(theme) => ({
                        fontSize: 11,
                        borderColor: theme.palette.warning.main,
                        color: theme.palette.warning.main,
                        bgcolor: alpha(theme.palette.warning.main, 0.08),
                      })}
                    />
                  );
                })}
                {hiddenCount > 0 && !expanded && (
                  <Chip
                    icon={<ExpandMoreIcon sx={{ fontSize: 16 }} />}
                    label={`+${hiddenCount} ${t('more2')}`}
                    size='small'
                    color='warning'
                    variant='outlined'
                    onClick={() => setExpanded(true)}
                    sx={(theme) => ({
                      fontSize: 11,
                      borderStyle: 'dashed',
                      borderColor: alpha(theme.palette.warning.main, 0.6),
                      color: theme.palette.warning.dark,
                      bgcolor: alpha(theme.palette.warning.main, 0.04),
                    })}
                  />
                )}
                {expanded && hiddenCount > 0 && (
                  <Chip
                    icon={<ExpandLessIcon sx={{ fontSize: 16 }} />}
                    label={t('ShowLess')}
                    size='small'
                    color='warning'
                    variant='outlined'
                    onClick={() => setExpanded(false)}
                    sx={(theme) => ({
                      fontSize: 11,
                      borderStyle: 'dashed',
                      borderColor: alpha(theme.palette.warning.main, 0.6),
                      color: theme.palette.warning.dark,
                      bgcolor: alpha(theme.palette.warning.main, 0.04),
                    })}
                  />
                )}
              </Stack>
            </>
          )}
        </Box>
      )}
    </>
  );
};

MainPageTags.defaultProps = {
  show: true,
  tags: undefined,
  businessAreaTags: undefined,
};
